import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, Code2, Cpu, Award, FileCheck2, Lock, GitBranch,
  Upload, Zap, Eye, ArrowRight, AlertOctagon, ShieldAlert,
  CircleDot, ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultCtaBand from "../components/ConsultCtaBand";
import ConsultationModal from "../components/ConsultationModal";
import AuditCisoCallout from "../components/audit/AuditCisoCallout";
import StickyToc from "../components/StickyToc";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

const SAMPLE_CERT_URL = "/api/audit/sample-2026-vault/certificate.svg";
const SAMPLE_REPORT_HTML_URL = "/api/audit/sample-2026-vault/certificate.html";

// Two product tiers — kept in one place so the FE table + the FAQ stay in sync.
const TIERS = [
  {
    id: "standard",
    name: "Standard Audit",
    tagline: "AI-powered. ~30 seconds. Production-ready report.",
    turnaround: "≤ 2 minutes",
    review: "AI engine only",
    bestFor: "Pre-launch sanity check · Solo dev · Hackathon submission",
    bullets: [
      "Findings ranked Critical → Info with code excerpts + remediation",
      "10-point Trust Score on industry best-practices",
      "Numerical Security Score (0–100)",
      "Shareable certificate (SVG / PNG / HTML) ready to embed",
      "Re-audit after fixes — score updates in real time",
    ],
    cta: "Submit a Standard Audit",
    accent: "#00E5FF",
  },
  {
    id: "deep_dive",
    name: "Deep Dive Audit",
    tagline: "AI surfaces it, a senior auditor finalizes it.",
    turnaround: "1–2 weeks",
    review: "AI + Human reviewer",
    bestFor: "Mainnet launch · Treasury vault · Cross-chain bridge · Institutional clients",
    bullets: [
      "Everything in Standard, plus:",
      "Manual code-path tracing & threat modelling",
      "Economic exploit analysis (MEV, oracle abuse, governance)",
      "Cross-contract reasoning (multi-file, upgradeability, hooks)",
      "Branded PDF report + auditor sign-off",
      "1× free re-audit after remediation",
    ],
    cta: "Request a Deep Dive",
    accent: "#FFFFFF",
  },
];

const SEVERITIES = [
  { name: "CRITICAL", color: "#FF6E6E", desc: "Direct loss-of-funds, unrestricted privileged action, or universal exploit." },
  { name: "HIGH",     color: "#FF8000", desc: "Loss-of-funds path with constraints; upgradability or governance break." },
  { name: "MEDIUM",   color: "#FFB000", desc: "Defect requiring unusual conditions; significant DoS." },
  { name: "LOW",      color: "#1FDD83", desc: "Best-practice deviation with minor security impact." },
  { name: "INFO",     color: "#5D7394", desc: "Code-quality / readability observation; no security impact." },
];

const TRUST_CHECKS = [
  "Access control modifiers (onlyOwner, RBAC)",
  "Events emitted on state-changing functions",
  "Re-entrancy protection (Guard / CEI pattern)",
  "Safe arithmetic (Solidity 0.8+ checks, no unchecked)",
  "Input validation (require / assert)",
  "No hardcoded secrets or private keys",
  "No tx.origin used for authentication",
  "Error handling with custom errors / messages",
  "NatSpec / docstring documentation",
  "Test files referenced in repo",
];

const CHAINS = [
  "Solidity (Ethereum, Polygon, Arbitrum, Optimism, Base, BNB Chain)",
  "Move (Aptos, Sui)",
  "Rust / CosmWasm (Solana, Cosmos: Osmosis, Celestia, Sei, Injective)",
  "Cairo (Starknet)",
  "Vyper (Ethereum)",
  "Tact / FunC (TON)",
  "Clarity (Stacks)",
];

export default function Audit() {
  const { t } = useTranslation();
  usePageMeta(M.audit);
  const [showConsult, setShowConsult] = useState(false);

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="audit-page">
      <Header onConsultClick={() => setShowConsult(true)} />
      <StickyToc containerSelector=".qt-doc-content" />
      <div className="qt-doc-content">

      {/* HERO ============================================================= */}
      <section className="relative qt-grain">
        <div className="absolute inset-0 qt-hero-grid pointer-events-none" />
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 pt-14 sm:pt-20 pb-12 relative">
          <div
            data-testid="audit-hero-pill"
            className="inline-flex items-center gap-2 mb-5 qt-mono text-xs px-3 py-1.5 rounded-full"
            style={{
              background: "rgba(0,229,255,0.06)",
              color: "var(--qt-cyan)",
              border: "1px solid var(--qt-cyan)",
              letterSpacing: "0.05em",
            }}
          >
            <span className="qt-pulse" style={{ width: 6, height: 6, borderRadius: 99, background: "var(--qt-cyan)" }} />
            QORTRACE AUDIT · NOW ACCEPTING SUBMISSIONS
          </div>

          <h1 className="qt-display text-[36px] sm:text-[52px] xl:text-[64px]"
              style={{ lineHeight: 1.04, letterSpacing: "-0.015em" }}>
            {t("audit.h1_line1", "Post-Quantum Audits")}<br />
            <span style={{ color: "#00E5FF" }}>{t("audit.h1_line2", "for the Onchain Economy.")}</span>
          </h1>

          <p className="mt-6 text-lg max-w-3xl"
             style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
            {t("audit.subtitle_lead", "QorTrace audits are")}{" "}
            <strong style={{ color: "#FFFFFF" }}>
              {t("audit.subtitle_scope", "scoped exclusively to post-quantum cryptographic risk")}
            </strong>{" "}
            {t("audit.subtitle_body", "— the threat your existing security audit doesn't cover. Across 18 chains, we map every signature scheme, key derivation, and pairing operation your contract depends on, score its")}{" "}
            <strong style={{ color: "#FFFFFF" }}>
              {t("audit.subtitle_score", "Quantum Exposure")}
            </strong>
            {t("audit.subtitle_tail", ", and deliver a signed, customer-viewable certificate the moment your audit is complete.")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/audit/preview"
              data-testid="audit-cta-preview"
              className="qt-btn qt-btn-primary"
              style={{ fontSize: 14, padding: "0.75rem 1.25rem" }}
            >
              Try a free preview <ArrowRight size={14} />
            </Link>
            <Link
              to="/account/audits/new"
              data-testid="audit-cta-submit"
              className="qt-btn qt-btn-secondary"
              style={{ fontSize: 14, padding: "0.75rem 1.25rem" }}
            >
              Submit an Audit <ArrowRight size={14} />
            </Link>
            <a
              href={SAMPLE_REPORT_HTML_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="audit-cta-sample"
              className="qt-btn qt-btn-ghost"
              style={{ fontSize: 14, padding: "0.75rem 1.25rem", border: "1px solid var(--qt-border)" }}
            >
              <Eye size={14} /> See a sample audit
            </a>
            <button
              type="button"
              onClick={() => setShowConsult(true)}
              data-testid="audit-cta-talk"
              className="qt-btn qt-btn-ghost"
              style={{ fontSize: 14, padding: "0.75rem 1.25rem", border: "1px solid var(--qt-border)" }}
            >
              Talk to an auditor
            </button>
          </div>

          {/* Stat strip */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="audit-stats">
            <Stat label={t("audit.stats_chains", "CHAINS COVERED")}    value="18" />
            <Stat label={t("audit.stats_severity", "SEVERITY TIERS")}    value="5" sub={t("audit.stats_severity_sub", "C · H · M · L · Info")} />
            <Stat label={t("audit.stats_primitives", "PQC PRIMITIVES")}    value="10+" sub={t("audit.stats_primitives_sub", "ECDSA · EdDSA · BLS · HKDF · …")} />
            <Stat label={t("audit.stats_turnaround", "STANDARD TURNAROUND")} value="≤2 min" sub={t("audit.stats_turnaround_sub", "AI-powered")} />
          </div>

          {/* SCOPE PANEL — explicit "what we cover / what we don't" so
              buyers never confuse our PQC audit for a general security audit. */}
          <div
            data-testid="audit-scope-panel"
            className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div
              className="qt-card"
              style={{ border: "1px solid var(--qt-cyan)", background: "rgba(0,229,255,0.04)" }}
            >
              <div className="qt-mono text-xs mb-2"
                   style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
                {t("audit.scope_covers_label", "✓ WHAT QORTRACE COVERS")}
              </div>
              <ul className="space-y-1.5 text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                <li>• {t("audit.scope_covers_item_1", "Every cryptographic primitive your contract depends on (ECDSA, EdDSA, BLS, Schnorr, HKDF, …)")}</li>
                <li>• {t("audit.scope_covers_item_2", "Harvest-now-decrypt-later exposure on every signature surface")}</li>
                <li>• {t("audit.scope_covers_item_3", "Hybrid signing readiness · domain separation · downgrade resistance")}</li>
                <li>• {t("audit.scope_covers_item_4", "PQC migration playbook keyed to NIST FIPS 203 / 204 / 205")}</li>
                <li>• {t("audit.scope_covers_item_5", "CNSA 2.0 / DORA / FFIEC compliance evidence")}</li>
              </ul>
            </div>
            <div
              className="qt-card"
              style={{ border: "1px solid var(--qt-border)", background: "rgba(255,255,255,0.02)" }}
            >
              <div className="qt-mono text-xs mb-2"
                   style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em" }}>
                {t("audit.scope_excludes_label", "— WHAT WE DON'T COVER")}
              </div>
              <ul className="space-y-1.5 text-sm" style={{ color: "var(--qt-text-3)", lineHeight: 1.55 }}>
                <li>• {t("audit.scope_excludes_item_1", "Re-entrancy, integer overflow, access control bugs")}</li>
                <li>• {t("audit.scope_excludes_item_2", "Business-logic flaws, oracle manipulation, MEV vectors")}</li>
                <li>• {t("audit.scope_excludes_item_3", "Generic Solidity static analysis")}</li>
              </ul>
              <p className="mt-3 text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                {t("audit.scope_pairing_note", "For general smart-contract security, we recommend pairing a QorTrace PQC audit with a review from an established general-security audit firm. Our certificate stacks cleanly alongside theirs.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Iter270 — CISO callout. Surfaces the PQC Audit Suite to
          developer-tier visitors who are actually CISO buyers. */}
      <AuditCisoCallout />

      {/* TIERS ============================================================ */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 py-16">
        <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
          STEP 1 · CHOOSE YOUR TIER
        </div>
        <h2 className="qt-display text-3xl sm:text-4xl mb-3" style={{ letterSpacing: "-0.01em" }}>
          Two tiers, one engine.
        </h2>
        <p className="max-w-2xl text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
          Standard delivers a production-ready AI report in under two minutes.
          Deep Dive layers a senior human auditor on top — for code that's about
          to be deployed to mainnet, hold institutional capital, or face adversarial users.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {TIERS.map((t) => (
            <TierCard key={t.id} tier={t} onConsult={() => setShowConsult(true)} />
          ))}
        </div>
      </section>

      {/* HOW IT WORKS ===================================================== */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 py-12">
        <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
          STEP 2 · SUBMIT
        </div>
        <h2 className="qt-display text-3xl sm:text-4xl mb-10" style={{ letterSpacing: "-0.01em" }}>
          Three ways to send us code.
        </h2>

        <div className="grid md:grid-cols-3 gap-5">
          <SubmitMode
            icon={<Code2 size={24} />}
            title="Paste raw code"
            body="Drop your .sol / .move / .rs / .cairo file straight into the textarea. Best for quick checks of a single contract."
          />
          <SubmitMode
            icon={<GitBranch size={24} />}
            title="GitHub URL"
            body="Paste a link to a single file, folder, or whole repo. We pull via the public GitHub API and scope to auditable extensions only."
          />
          <SubmitMode
            icon={<Upload size={24} />}
            title="Upload a .zip"
            body="Multi-file projects, monorepos, or anything off-platform. Up to 200KB of source per audit (Deep Dive: 600KB)."
            soon
          />
        </div>
      </section>

      {/* SCORING METHOD =================================================== */}
      <section className="qt-grain">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 py-16">
          <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
            STEP 3 · SCORED
          </div>
          <h2 className="qt-display text-3xl sm:text-4xl mb-3" style={{ letterSpacing: "-0.01em" }}>
            Two scores. Zero ambiguity.
          </h2>
          <p className="max-w-2xl text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
            Every QorTrace audit produces two numbers — auditable, reproducible, defensible to a CISO. Both are recomputed in real time as you mark findings as fixed or acknowledged in the dashboard.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="qt-card" data-testid="security-score-method">
              <div className="qt-mono text-xs mb-2" style={{ color: "#FF8000", letterSpacing: "0.18em" }}>SECURITY SCORE · 0–100</div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "#FFFFFF" }}>
                Penalised by severity weight.
              </h3>
              <p className="text-sm mb-4" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                Every open finding subtracts points from a baseline of 100. Industry-norm weights mirror Trail of Bits / Consensys Diligence:
              </p>
              <div className="space-y-1.5 qt-mono text-xs" style={{ letterSpacing: "0.06em" }}>
                <ScorePill color="#FF6E6E" label="CRITICAL" weight="−25" />
                <ScorePill color="#FF8000" label="HIGH"     weight="−10" />
                <ScorePill color="#FFB000" label="MEDIUM"   weight="−4" />
                <ScorePill color="#1FDD83" label="LOW"      weight="−1" />
                <ScorePill color="#5D7394" label="INFO"     weight="0" />
              </div>
            </div>

            <div className="qt-card" data-testid="trust-score-method">
              <div className="qt-mono text-xs mb-2" style={{ color: "#1FDD83", letterSpacing: "0.18em" }}>TRUST SCORE · 0–100</div>
              <h3 className="text-xl font-bold mb-3" style={{ color: "#FFFFFF" }}>
                10 best-practice axes. Each pass = 10 points.
              </h3>
              <p className="text-sm mb-4" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                Beyond exploit-class findings, we also evaluate engineering hygiene:
              </p>
              <ul className="space-y-1.5 text-sm" style={{ color: "var(--qt-text-2)" }}>
                {TRUST_CHECKS.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <CircleDot size={12} className="mt-1 flex-shrink-0" style={{ color: "var(--qt-cyan)" }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Severity tier glossary */}
          <div className="mt-10 qt-card" data-testid="severity-glossary">
            <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>SEVERITY GLOSSARY</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
              {SEVERITIES.map((s) => (
                <div key={s.name} className="rounded p-3" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${s.color}33` }}>
                  <div className="qt-mono text-xs mb-1.5" style={{ color: s.color, letterSpacing: "0.18em", fontWeight: 700 }}>
                    {s.name}
                  </div>
                  <p className="text-xs" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATE SHOWCASE ============================================= */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 py-16">
        <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
          STEP 4 · DELIVERED
        </div>
        <h2 className="qt-display text-3xl sm:text-4xl mb-3" style={{ letterSpacing: "-0.01em" }}>
          Your shareable certificate.
        </h2>
        <p className="max-w-3xl text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
          Every delivered audit gets a public, branded certificate at a permanent URL.
          Embed it on your README, docs site, or pitch deck. Upload your project logo
          and we'll auto-fill it on the cert alongside the QorTrace mark — co-branded,
          credibility instant.
        </p>

        <div className="mt-8 qt-card" data-testid="audit-sample-cert" style={{ padding: 0, overflow: "hidden" }}>
          <div className="p-3 flex items-center justify-between flex-wrap gap-2 border-b" style={{ borderColor: "var(--qt-border)" }}>
            <div className="qt-mono text-xs" style={{ color: "var(--qt-text-2)", letterSpacing: "0.16em" }}>
              SAMPLE CERTIFICATE · DEMOVAULT.SOL · LIVE FROM /api/audit/sample-2026-vault
            </div>
            <span
              data-testid="sample-cert-fullscreen"
              aria-disabled="true"
              title="Disabled — sample preview only. Visit qortrace.com to verify any cert."
              className="qt-mono text-xs"
              style={{ color: "var(--qt-text-3)", letterSpacing: "0.12em",
                       textDecoration: "line-through",
                       textDecorationColor: "rgba(255,255,255,0.35)",
                       cursor: "not-allowed", opacity: 0.55 }}
            >
              ⛔ OPEN FULL · disabled
            </span>
          </div>
          <img
            src={SAMPLE_CERT_URL}
            alt="QorTrace Audit Certificate — DemoVault.sol"
            data-testid="sample-cert-img"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            style={{ display: "block", width: "100%", height: "auto",
                     background: "#040914", pointerEvents: "none",
                     userSelect: "none" }}
          />
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <Bullet icon={<Award size={16} />} title="Three formats" body="SVG · PNG · standalone HTML page with OG tags for social previews." />
          <Bullet icon={<Lock size={16} />} title="Live & verifiable" body="Every cert URL pulls from the live audit row — anyone can verify it hasn't been tampered with." />
          <Bullet icon={<ShieldCheck size={16} />} title="Co-branded" body="Upload your logo at submission. We auto-render it next to the QorTrace mark on every cert." />
        </div>
      </section>

      {/* WHAT WE AUDIT (CHAINS) =========================================== */}
      <section className="qt-grain">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 py-16">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
                COVERAGE
              </div>
              <h2 className="qt-display text-3xl sm:text-4xl mb-4" style={{ letterSpacing: "-0.01em" }}>
                The full 18-chain matrix.
              </h2>
              <p className="text-base" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
                If your code runs on a public blockchain, we can audit it. Same engine, same scoring system, across every major smart-contract VM:
              </p>
              <ul className="mt-6 space-y-2" style={{ color: "var(--qt-text-2)" }}>
                {CHAINS.map((c) => (
                  <li key={c} className="flex items-start gap-2 text-sm">
                    <ChevronRight size={14} className="mt-1 flex-shrink-0" style={{ color: "var(--qt-cyan)" }} />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>
                INSTITUTIONAL EDGE
              </div>
              <h2 className="qt-display text-3xl sm:text-4xl mb-4" style={{ letterSpacing: "-0.01em" }}>
                We see what others miss.
              </h2>
              <div className="space-y-4">
                <Edge icon={<Cpu size={18} />} title="Quantum-readiness baked in"
                      body="Every audit also checks for cryptographic primitives that NIST has flagged as quantum-vulnerable. We're the only auditor whose deliverable answers both 'is this secure today?' AND 'will this still be secure in 2030?'" />
                <Edge icon={<ShieldAlert size={18} />} title="Institutional vulnerability checks"
                      body="Beyond OWASP-style classes, we cover patterns that bite custodians, exchanges, and treasuries: cold-key handling, signing-scheme selection, multisig threshold sanity, oracle dependency graphs, and bridge re-org safety." />
                <Edge icon={<FileCheck2 size={18} />} title="Reproducible methodology"
                      body="Public scoring formula. Public severity weights. Public trust-check definitions. Your auditor's findings can be cross-checked by anyone on your team — or your investors." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ ============================================================== */}
      <section className="max-w-[1200px] mx-auto px-6 sm:px-8 py-16">
        <div className="qt-mono text-xs mb-3" style={{ color: "var(--qt-cyan)", letterSpacing: "0.18em" }}>FAQ</div>
        <h2 className="qt-display text-3xl sm:text-4xl mb-10" style={{ letterSpacing: "-0.01em" }}>
          The questions every team asks.
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          <FAQ q="Is the AI-only Standard tier enough for mainnet?"
               a="For most pre-launch sanity checks, yes — Standard catches the same exploit classes a senior human would catch in a first-pass review. For mainnet code that holds significant capital or has adversarial economic exposure (bridges, perps, lending), upgrade to Deep Dive — our human auditors do a manual code-path trace and threat-model the economic surface." />
          <FAQ q="What happens to my source code?"
               a="It's stored encrypted in our database, never logged externally, and only readable by the auditor assigned to your audit. Source is automatically purged 90 days after delivery unless you opt in to retention for re-audit support." />
          <FAQ q="Can I re-audit after fixing the findings?"
               a="Yes — that's the point. Every audit is a live document. Mark findings 'fixed' or 'acknowledged' from your dashboard and your Security Score recomputes in real time. Deep Dive includes one free re-audit; Standard re-audits are flat-rated." />
          <FAQ q="What languages and chains do you cover?"
               a="Solidity (all EVM chains: Ethereum, Polygon, Arbitrum, Optimism, Base, BNB), Move (Aptos, Sui), Rust/CosmWasm (Solana, Cosmos hubs), Cairo (Starknet), Vyper, Tact/FunC (TON), and Clarity (Stacks). 18 chains in total." />
          <FAQ q="Do you publish findings publicly?"
               a="Never without your explicit consent. The certificate is public by default (you'll want to embed it). The full findings dashboard is private to your account. We only publish anonymised pattern statistics in our methodology research." />
          <FAQ q="Can I get a co-branded report?"
               a="Yes — upload your project logo at submission and we'll auto-fill it on the certificate next to the QorTrace mark. Deep Dive customers also get their logo on the PDF report cover page." />
        </div>
      </section>

      {/* CTA BAND ========================================================== */}
      <ConsultCtaBand source="audit-page-bottom" />

      </div>  {/* end .qt-doc-content */}

      <Footer />

      <ConsultationModal
        open={showConsult}
        onClose={() => setShowConsult(false)}
        source="audit-page"
      />
    </div>
  );
}

// ============================================================================
// Subcomponents
// ============================================================================

function Stat({ label, value, sub }) {
  return (
    <div className="qt-card" style={{ padding: "1rem 1.25rem" }} data-testid={`audit-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="qt-mono text-xs mb-1" style={{ color: "var(--qt-text-2)", letterSpacing: "0.18em" }}>{label}</div>
      <div className="text-3xl font-extrabold" style={{ color: "#00E5FF", lineHeight: 1 }}>{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: "var(--qt-text-2)" }}>{sub}</div>}
    </div>
  );
}

function TierCard({ tier, onConsult }) {
  const isDeepDive = tier.id === "deep_dive";
  const handleClick = isDeepDive
    ? onConsult
    : () => { window.location.href = "/account"; };
  return (
    <div className="qt-card relative" data-testid={`tier-${tier.id}`}
         style={{ borderColor: isDeepDive ? "var(--qt-cyan)" : undefined }}>
      <div className="qt-mono text-xs mb-2" style={{ color: tier.accent, letterSpacing: "0.18em" }}>
        {tier.name.toUpperCase()}
      </div>
      <h3 className="text-2xl font-bold mb-2" style={{ color: "#FFFFFF", letterSpacing: "-0.01em" }}>
        {tier.tagline}
      </h3>

      <div className="grid grid-cols-2 gap-3 my-4 py-3 border-y" style={{ borderColor: "var(--qt-border)" }}>
        <KV label="TURNAROUND" value={tier.turnaround} />
        <KV label="REVIEW"     value={tier.review} />
      </div>

      <div className="qt-mono text-[10px] mb-3" style={{ color: "var(--qt-text-2)", letterSpacing: "0.16em" }}>
        BEST FOR
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>
        {tier.bestFor}
      </p>

      <ul className="space-y-2 mb-6">
        {tier.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
            <CircleDot size={12} className="mt-1.5 flex-shrink-0" style={{ color: tier.accent }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={handleClick}
        data-testid={`tier-cta-${tier.id}`}
        className={isDeepDive ? "qt-btn qt-btn-secondary" : "qt-btn qt-btn-primary"}
        style={{ width: "100%", justifyContent: "center", padding: "0.75rem", fontSize: 14 }}
      >
        {tier.cta} <ArrowRight size={14} />
      </button>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="qt-mono text-[10px]" style={{ color: "var(--qt-text-2)", letterSpacing: "0.16em" }}>{label}</div>
      <div className="text-sm font-bold mt-1" style={{ color: "#FFFFFF" }}>{value}</div>
    </div>
  );
}

function SubmitMode({ icon, title, body, soon }) {
  return (
    <div className="qt-card relative" data-testid={`submit-mode-${title.toLowerCase().replace(/\s/g, '-')}`}>
      {soon && (
        <div className="absolute top-3 right-3 qt-mono text-[9px] px-2 py-0.5 rounded"
             style={{ background: "rgba(0,229,255,0.1)", color: "var(--qt-cyan)", letterSpacing: "0.16em", border: "1px solid var(--qt-cyan)" }}>
          SOON
        </div>
      )}
      <div className="mb-3" style={{ color: "var(--qt-cyan)" }}>{icon}</div>
      <h3 className="text-lg font-bold mb-2" style={{ color: "#FFFFFF" }}>{title}</h3>
      <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

function ScorePill({ color, label, weight }) {
  return (
    <div className="flex items-center justify-between rounded px-3 py-2"
         style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${color}33` }}>
      <span style={{ color, fontWeight: 700 }}>{label}</span>
      <span style={{ color, fontWeight: 800 }}>{weight}</span>
    </div>
  );
}

function Bullet({ icon, title, body }) {
  return (
    <div className="qt-card">
      <div className="flex items-start gap-3 mb-2" style={{ color: "var(--qt-cyan)" }}>
        {icon}
        <div className="font-bold text-base" style={{ color: "#FFFFFF" }}>{title}</div>
      </div>
      <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function Edge({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 mt-1" style={{ color: "var(--qt-cyan)" }}>{icon}</div>
      <div>
        <div className="font-bold text-base mb-1" style={{ color: "#FFFFFF" }}>{title}</div>
        <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>{body}</p>
      </div>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <div className="qt-card" data-testid={`faq-${q.slice(0, 12).toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <div className="font-bold mb-2 text-base" style={{ color: "#FFFFFF" }}>{q}</div>
      <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>{a}</p>
    </div>
  );
}
